import type { Edge, Node } from '@xyflow/react';
import type {
  AspectRatio,
  IEpisode,
  IUserModel,
  IProjectAsset,
  IVideoClip,
  IVideoClipResource,
  VideoEditConfig,
} from '@packages/global';

export type ClipImageTarget = 'start' | 'end' | 'storyboard';

export type ClipPreviewEditorTarget = 'audio' | 'effects' | 'video';

export type ClipCanvasMediaTarget = ClipImageTarget | ClipPreviewEditorTarget;

export type ClipCanvasMediaType = 'image' | 'audio' | 'effects' | 'video';

export type ClipCanvasResourceMediaType = 'image' | 'audio';

export type ClipResourceGroupType = 'image' | 'audio' | 'video';

export type ClipCanvasFormType =
  | 'start-image'
  | 'end-image'
  | 'storyboard-image'
  | 'audio'
  | 'audio-edit'
  | 'video'
  | 'lip-sync';

export type ClipCanvasNodeKind =
  | 'clip-context'
  | 'applied-media'
  | 'candidate-resource'
  | 'form-generator'
  | 'setting-asset'
  | 'media-resource';

/** 生成器节点稳定 id */
export const IMAGE_GENERATOR_NODE_ID = 'generator:image';

export type ClipGeneratorNodeId =
  | typeof IMAGE_GENERATOR_NODE_ID
  | string;

/** 画布交互回调（图片节点上的快捷按钮等） */
export interface ClipCanvasNodeActions {
  /** 把图片 URL 作为参考图注入到指定生成器节点 */
  onUseAsReference: (url: string, target?: ClipGeneratorNodeId) => void;
  /** 打开指定节点的属性编辑面板 */
  onEditNode?: (nodeId: string) => void;
  /** 打开画面编辑弹窗并定位到指定帧 */
  onEditImageFrame?: (target: ClipImageTarget) => void;
  /** 打开预览区中对应的编辑弹窗 */
  onEditPreviewPanel?: (target: ClipPreviewEditorTarget) => void;
  /** 应用候选图片到指定帧 */
  onApplyImageResource?: (imageUrl: string, target: ClipImageTarget) => Promise<void> | void;
  /** 应用候选配音到当前片段 */
  onApplyAudioResource?: (audioUrl: string) => Promise<void> | void;
  /** 应用候选视频到当前片段 */
  onApplyVideoResource?: (videoUrl: string) => Promise<void> | void;
  /** 基于候选资源的生成参数创建一个表单节点 */
  onCreateFormFromResource?: (resource: IVideoClipResource) => Promise<void> | void;
  /** 复制候选资源提示词 */
  onCopyResourcePrompt?: (resource: IVideoClipResource) => Promise<void> | void;
  /** 删除候选资源 */
  onDeleteResource?: (resourceId: string) => Promise<void> | void;
  /** 删除画布内临时表单节点 */
  onDeleteFormNode?: (nodeId: string) => Promise<void> | void;
  /** 无确认移除画布内临时表单节点，用于表单提交成功后的自动清理 */
  onRemoveFormNode?: (nodeId: string) => Promise<void> | void;
  /** 删除画布内设定节点 */
  onDeleteSettingNode?: (nodeId: string) => Promise<void> | void;
  /** 删除画布内独立媒体资源节点 */
  onDeleteMediaResourceNode?: (nodeId: string) => Promise<void> | void;
  /** 表单内清空参考媒体时，同步移除对应画布连线 */
  onReferenceMediaCleared?: (targetNodeId: string, url: string, mediaType: ClipCanvasResourceMediaType) => void;
}

export interface ClipContextNodeData extends Record<string, unknown> {
  kind: 'clip-context';
  videoClip: IVideoClip;
  actions: ClipCanvasNodeActions;
}

export interface AppliedMediaNodeData extends Record<string, unknown> {
  kind: 'applied-media';
  id: string;
  label: string;
  mediaType: ClipCanvasMediaType;
  url?: string;
  target: ClipCanvasMediaTarget;
  summary?: string;
  actions: ClipCanvasNodeActions;
}

export interface CandidateResourceNodeData extends Record<string, unknown> {
  kind: 'candidate-resource';
  label: string;
  groupType: ClipResourceGroupType;
  resources: IVideoClipResource[];
  actions: ClipCanvasNodeActions;
}

export interface FormGeneratorNodeData extends Record<string, unknown> {
  kind: 'form-generator';
  formType: ClipCanvasFormType;
  title: string;
  initialParams?: Record<string, unknown>;
  actions?: ClipCanvasNodeActions;
}

export interface SettingAssetNodeData extends Record<string, unknown> {
  kind: 'setting-asset';
  asset: IProjectAsset;
  imageUrl?: string;
  audioUrl?: string;
  actions?: ClipCanvasNodeActions;
}

export interface MediaResourceNodeData extends Record<string, unknown> {
  kind: 'media-resource';
  mediaType: ClipCanvasResourceMediaType;
  title: string;
  url: string;
  sourceLabel: string;
  prompt?: string;
  actions?: ClipCanvasNodeActions;
}

export type ClipCanvasNodeData =
  | ClipContextNodeData
  | AppliedMediaNodeData
  | CandidateResourceNodeData
  | FormGeneratorNodeData
  | SettingAssetNodeData
  | MediaResourceNodeData;

export type ClipCanvasNode = Node<ClipCanvasNodeData>;

export type ClipCanvasEdge = Edge;

export interface BuildClipCanvasGraphOptions {
  videoClip: IVideoClip;
  resources: IVideoClipResource[];
  actions: ClipCanvasNodeActions;
  previousNodes?: ClipCanvasNode[];
  mediaAspectRatio?: AspectRatio;
}

export interface ClipCanvasGraph {
  nodes: ClipCanvasNode[];
  edges: ClipCanvasEdge[];
}

/** 参考图注入事件：nonce 变化即触发一次注入 */
export interface ReferenceInjectEvent {
  url: string;
  nonce: number;
  mediaType?: 'image' | 'audio';
}

/** 画布共享上下文：生成器节点从此读取复用现有表单所需的全部 props */
export interface ClipCanvasContextValue {
  videoClip: IVideoClip;
  episode: IEpisode | null;
  models: IUserModel[];
  modelsLoading: boolean;
  editConfig: VideoEditConfig | null;
  onEditConfigChange?: (config: VideoEditConfig) => void;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  resources: IVideoClipResource[];
  isPolling: boolean;
  onTriggerResourcePoll?: () => Promise<void>;
  /** 各生成器节点当前的参考图注入事件 */
  injectEvents: Record<string, ReferenceInjectEvent | undefined>;
  /** 请求向某个生成器节点注入一份参考媒体 */
  requestInjectReference: (target: ClipGeneratorNodeId, url: string, mediaType?: 'image' | 'audio') => void;
}
